import { extensions } from "@repo/fika-blog/src/extensions"
import { renderToText } from "@repo/fika-blog/src/renderer/pm/text/text"
import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import { format, intlFormatDistance } from "date-fns"
import { createMemo, Show } from "solid-js"
import { db } from "@/data/database"
import type { SearchResult } from "@/data/search-index"
import { Box } from "@/designSystem/components/box"
import { Text } from "@/designSystem/components/text"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/getTitle"
import { generateSearchHighlighter } from "@/lib/searchHighlight"
import { Icon } from "../components/icon"
import { Logo } from "../components/logo"
import { Mark } from "../components/mark"
import { type Pill, Pills } from "./pills"
import { postClass, postTitleClass } from "./post.css"
import { Quote } from "./quote"

type Props = {
  post: Post
  searchResult?: SearchResult | null
  hideLogo?: boolean
}

export function PostComponent(props: Props) {
  const title = createMemo(() => getTitle(props.post.title))
  const hl = generateSearchHighlighter(props.searchResult)
  const { LL } = useI18nContext()

  const titleHl = createMemo(() => hl("title", title()))
  const descriptionHl = createMemo(() =>
    hl("description", props.post.description),
  )
  const truncatedBody = createMemo(() => {
    const json = props.post.json
    if (!json) return ""

    try {
      return renderToText({
        extensions: extensions({ dropcursor: false }),
        doc: JSON.parse(json),
      }).slice(0, 300)
    } catch (e) {
      console.error(e)
      return ""
    }
  })

  const contentHl = createMemo(() => hl("body"))
  const postDate = createMemo(
    () => props.post.published_at || props.post.created_at,
  )
  const humanReadableDate = createMemo(() => {
    const date = postDate()
    if (!date) return null
    const verb = props.post.published_at ? "published" : "created"

    return `${verb} ${intlFormatDistance(date, new Date())}`
  })
  const formatDate = createMemo(() => {
    const date = postDate()
    if (!date) return undefined

    return format(date, "PPp")
  })
  const postStat = createMemo(() => db.postStat.get(props.post.id))
  const getBookmark = createMemo(() => {
    const bookmarkId = props.post.bookmark_id
    if (!bookmarkId) return undefined

    return db.bookmark.get(bookmarkId)
  })

  const pills = createMemo(() => {
    const pills: Pill[] = []

    if (getBookmark()) {
      pills.push({
        icon: "book-text",
        label: "blogmark",
        hue: "amber",
      })
    } else {
      pills.push({
        icon: "pen-tool",
        label: "post",
        hue: "amber",
      })
    }

    pills.push(postStatusPill(props.post))

    const tags = db.tag.filterBy({
      target_id: props.post.id,
      target_type: "post",
    })

    for (const tag of tags) {
      pills.push({
        label: tag.tag,
        rounded: true,
        hue: "sand",
      })
    }

    return pills
  })

  return (
    <div class={postClass}>
      <Box gap="16" fill>
        <Box gap="8" fill>
          <Box>
            <Box alignItems="center" flexDirection="row" gap="8" fill>
              <span class={postTitleClass}>
                <Mark hue="amber" pieces={titleHl()} />
              </span>
            </Box>
            <Text fontSize="s" color="sand800" lineClamp="1">
              <time title={formatDate()} datetime={formatDate()}>
                {humanReadableDate()}
              </time>
              {" · "}
              {LL().common.countViews(postStat()?.recentViews ?? 0)}
            </Text>
          </Box>
          <Text fontSize="s" color="sand900" lineClamp="3">
            <Show when={descriptionHl().length} fallback={truncatedBody()}>
              <Mark hue="amber" pieces={descriptionHl()} />
            </Show>
          </Text>
          <Quote pieces={contentHl()} />
          <Pills pills={pills()} />
        </Box>
      </Box>
      <Show when={!props.hideLogo}>
        <Logo
          image={{ url: props.post.image_header_url, imageClass: "thumb" }}
          srcset={[
            {
              url: props.post.image_header_url,
              imageClass: "smallThumb",
              breakpoint: "s",
            },
            {
              url: props.post.image_header_url,
              imageClass: "logo",
              breakpoint: "xs",
            },
          ]}
          fallback={<Icon name="pen-tool" color="amber600" />}
          alt={getTitle(props.post.title)}
        />
      </Show>
    </div>
  )
}

export function postStatusPill(post: Post): Pill {
  if (post.archived_at) {
    return {
      icon: "archive",
      label: "archived",
      hue: "orange",
    }
  }

  if (post.sent_at) {
    return {
      icon: "send",
      label: "sent",
      hue: "indigo",
    }
  }

  if (post.published_at) {
    return {
      icon: "book-up",
      label: "published",
      hue: "emerald",
    }
  }

  if (post.archived_at) {
    return {
      icon: "archive",
      label: "archived",
      hue: "orange",
    }
  }

  return {
    icon: "pencil",
    label: "draft",
    hue: "sand",
  }
}
